import React, { useRef, useState, useEffect } from "react";
import { Chip, Button, Grow, IconButton, InputAdornment, Paper, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CancelIcon from "@material-ui/icons/Cancel";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import { getAppColor } from "@icarius-app/selectors";
import ProfileImg from "@icarius-common/profileImg";
import { getLocalizedString } from "@icarius-localization/strings";
import IcariusIcon from "@icarius-assets/images/icariusIcon.png";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import CustomTextField from "./customTextField";
import Message from "./message";
import { DeleteIcon } from "@icarius-icons/index";
import useBotDraggable from "./useBotDraggable";

const ALLOWED_EXTENSIONS = [".txt", ".pdf", ".xlsx", ".docx", ".csv", ".bmp", ".jpg", ".jpeg", ".png"];
const ALLOWED_MIME_TYPES = [
    "text/plain",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/csv",
    "application/csv",
    "application/vnd.ms-excel",
    "image/bmp",
    "image/jpeg",
    "image/png",
];

const BotAssistant = (props) => {
    const {
        open,
        subtitle,
        isLoading,
        messages,
        handleSubmit,
        handleCancelSubmit,
        handleResetConversation,
        handleClose,
    } = props;

    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [isCancelling, setIsCancelling] = useState(false);

    const { dragRef, bounds } = useBotDraggable(open);

    const dispatch = useDispatch();
    const color = useSelector(getAppColor);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const lastMessageOptions = messages[messages.length - 1]?.options;
    const disableChat = isLoading || Boolean(lastMessageOptions?.length);

    const handleCancelClick = () => {
        if (isCancelling) return;
        setIsCancelling(true);
        handleCancelSubmit();
    };

    useEffect(() => {
        if (!isLoading) {
            setIsCancelling(false);
        }
    }, [isLoading]);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
            if (inputRef.current) inputRef.current.focus();
        }
    }, [messages, files, open]);

    const handleOpenFileInput = () => {
        if (!fileInputRef?.current) return;
        fileInputRef.current.click();
    };

    const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

    const isAllowedFile = (file) => {
        const name = file.name.toLowerCase();
        const hasValidExtension = ALLOWED_EXTENSIONS.some((extension) => name.endsWith(extension));
        const hasValidType = !file.type || ALLOWED_MIME_TYPES.includes(file.type);
        return hasValidExtension && hasValidType;
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files ?? []);
        if (!selectedFiles.length) return;

        if (selectedFiles.length + files.length > 2) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("cannotSelectMoreThanTwoFiles"), severity: "error" })
            );
            return;
        }

        const validFiles = selectedFiles.filter(isAllowedFile);
        const rejectedFiles = selectedFiles.filter((file) => !isAllowedFile(file));

        if (rejectedFiles.length) {
            dispatch(
                openSnackbarAction({
                    msg: `${getLocalizedString("fileInvalidFormat")}: ${rejectedFiles
                        .map((file) => file.name)
                        .join(", ")}`,
                    severity: "error",
                })
            );
        }

        setFiles((prev) => {
            const existingKeys = new Set(prev.map(getFileKey));
            const newFiles = validFiles.filter((file) => !existingKeys.has(getFileKey(file)));
            return [...prev, ...newFiles];
        });

        e.target.value = "";
        if (inputRef.current) inputRef.current.focus();
    };

    const handleRemoveFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const sendMessage = (messageText) => {
        if (!messageText.trim() && !files?.length) return;

        handleSubmit(messageText, files);
        setText("");
        setFiles([]);
    };

    const submit = (e) => {
        e.preventDefault();
        sendMessage(text);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            submit(e);
        }
    };

    return (
        <>
            {open && (
                <Draggable nodeRef={dragRef} handle=".bot-drag-handle" cancel="button" bounds={bounds}>
                    <div
                        ref={dragRef}
                        style={{
                            position: "fixed",
                            right: 10,
                            bottom: 10,
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 10,
                        }}
                    >
                        <Grow in={open} style={{ transformOrigin: "bottom right" }} unmountOnExit>
                            <Paper
                                role="dialog"
                                style={{
                                    borderRadius: 10,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "calc(100vh - 125px)",
                                    maxHeight: 700,
                                    width: 450,
                                    maxWidth: "calc(100vw - 20px)",
                                    overflow: "hidden",
                                    background: "transparent",
                                }}
                            >
                                <header
                                    className="bot-drag-handle"
                                    style={{
                                        background: color,
                                        padding: 10,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexShrink: 0,
                                        cursor: "move",
                                        userSelect: "none",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <ProfileImg image={IcariusIcon} size={40} external />
                                        <div>
                                            <Typography style={{ fontWeight: 600, color: "white" }}>
                                                {getLocalizedString("icariusAssistant")}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: "white" }}>
                                                {subtitle || getLocalizedString("howCanIHelp")}
                                            </Typography>
                                        </div>
                                    </div>
                                    <IconButton onClick={handleClose}>
                                        <CloseIcon style={{ fill: "white" }} />
                                    </IconButton>
                                </header>
                                <section
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        background: "var(--mainBackgroundColor)",
                                        border: "1px solid var(--secondaryBackgroundColor)",
                                        borderRadius: "0px 0px 10px 10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            padding: 10,
                                            overflowY: "auto",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 10,
                                        }}
                                    >
                                        {messages.map((message, index) => {
                                            return (
                                                <Message
                                                    key={index}
                                                    text={message.text}
                                                    status={message.status}
                                                    attachments={message.attachments}
                                                    time={message.time}
                                                    isUser={message.isUser}
                                                />
                                            );
                                        })}
                                        {isLoading && <Message isUser={false} />}
                                        {Boolean(lastMessageOptions?.length) && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    justifyContent: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                {lastMessageOptions.map((option, optionIndex) => {
                                                    return (
                                                        <Button
                                                            key={optionIndex}
                                                            onClick={() => sendMessage(option)}
                                                            variant="contained"
                                                            className="whiteText"
                                                            size="small"
                                                            style={{
                                                                margin: "0px 32px",
                                                                textTransform: "none",
                                                            }}
                                                            fullWidth
                                                        >
                                                            {option}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div ref={scrollRef} />
                                    </div>
                                    <footer
                                        style={{
                                            flexShrink: 0,
                                            padding: 10,
                                            paddingBottom: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        {Boolean(files?.length) && (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 5,
                                                    overflow: "auto",
                                                }}
                                            >
                                                {files.map((file, fileIndex) => {
                                                    return (
                                                        <Chip
                                                            key={`${file.name}-${file.lastModified}`}
                                                            label={file.name}
                                                            onDelete={() => handleRemoveFile(fileIndex)}
                                                            deleteIcon={
                                                                <CancelIcon style={{ fill: "var(--mainText)" }} />
                                                            }
                                                            size="small"
                                                            style={{
                                                                background: "var(--tertiaryBackgroundColor)",
                                                                border: "1px solid var(--borderColor)",
                                                            }}
                                                            className="whiteText"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {isLoading && (
                                            <Button
                                                onClick={handleCancelClick}
                                                disabled={isCancelling}
                                                variant="contained"
                                                className="whiteText"
                                                size="small"
                                                style={{
                                                    background: isCancelling
                                                        ? "var(--quaternaryBackgroundColor)"
                                                        : color,
                                                    border: "none",
                                                }}
                                                fullWidth
                                            >
                                                {getLocalizedString("cancelMessage")}
                                            </Button>
                                        )}
                                        {!isLoading && (
                                            <form onSubmit={submit} style={{ width: "100%" }} noValidate>
                                                <CustomTextField
                                                    inputRef={inputRef}
                                                    disabled={disableChat}
                                                    size={"small"}
                                                    placeholder={getLocalizedString("writeYourMessage")}
                                                    value={text}
                                                    onChange={(e) => setText(e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    multiline={true}
                                                    rowsMax={5}
                                                    fullWidth={true}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment
                                                                position="end"
                                                                style={{ display: "flex", gap: 5 }}
                                                            >
                                                                <IconButton
                                                                    title={getLocalizedString("deleteConversation")}
                                                                    edge="end"
                                                                    onClick={handleResetConversation}
                                                                    disabled={disableChat}
                                                                >
                                                                    <DeleteIcon style={{ fill: "var(--mainText)" }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    title={getLocalizedString("attachFile")}
                                                                    edge="end"
                                                                    onClick={handleOpenFileInput}
                                                                    disabled={disableChat}
                                                                >
                                                                    <AttachFileIcon
                                                                        style={{ fill: "var(--mainText)" }}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    title={getLocalizedString("sendMessage")}
                                                                    edge="end"
                                                                    type="submit"
                                                                    disabled={disableChat}
                                                                >
                                                                    <SendIcon style={{ fill: "var(--mainText)" }} />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileChange}
                                                    style={{ display: "none" }}
                                                    accept={ALLOWED_EXTENSIONS.join(",")}
                                                    multiple
                                                />
                                            </form>
                                        )}
                                        <Typography variant="caption" className="whiteText">
                                            {getLocalizedString("doNotShareInformation")}
                                        </Typography>
                                    </footer>
                                </section>
                            </Paper>
                        </Grow>
                    </div>
                </Draggable>
            )}
        </>
    );
};

export default BotAssistant;
